/**
 * Regulatory Database / Requirement Library (Sheet-2 rows 8 + 10; .claude/design/regulatory-database.md) -
 * pure UI-side helpers. Mirrors policylens-backend regulatoryContent.controller.ts (commits 5ef1b0f, a9d0110).
 *
 * Persona gating here is a UX affordance only; the API is the authority (every mutation and the admin views
 * are platform_admin-only, reads are platform_admin | lead_advisor | advisor | company_spoc, and a
 * company_delegate gets 403). Commandment IV: an AI draft reaches readers only after a human approves it,
 * bound to the exact draft by echoing `draftHash`; no model confidence is computed for this feature.
 */
import { Role } from '../constants/roles';
import { userMessageForError } from './apiError';
import { breakGlassReasonValid } from './questionBankCuratorModel';

export { breakGlassReasonValid };

export const canManageRegulatoryDatabase = (role) => role === Role.ADMIN;
export const canReadRegulatoryLibrary = (role) => [Role.LEAD_ADVISOR, Role.ADVISOR, Role.COMPANY_SPOC].includes(role);
export const canSeeApplicableRegulations = (role) => [Role.LEAD_ADVISOR, Role.ADVISOR].includes(role);

// Controlled vocabularies (regulatoryVocab.ts). The admin screen takes them from GET /regulatory-content/sources;
// reader screens have no such route, so they use these mirrors.
export const AUTHORITY_NAMES = Object.freeze(['RBI', 'SEBI', 'MCA', 'CERT-IN', 'MoLE', 'DoT', 'MeitY', 'IRDAI', 'CBIC', 'GST-Council', 'CBDT', 'EPFO', 'ESIC', 'State']);
export const DOCUMENT_TYPES = Object.freeze(['master_direction', 'circular', 'regulation', 'notification', 'advisory', 'act', 'rules']);

export const DOCUMENT_TYPE_LABELS = Object.freeze({
    master_direction: 'Master direction',
    circular: 'Circular',
    regulation: 'Regulation',
    notification: 'Notification',
    advisory: 'Advisory',
    act: 'Act',
    rules: 'Rules',
});
export const documentTypeLabel = (t) => DOCUMENT_TYPE_LABELS[t] || t || '';
export const documentTypeOptions = (types = DOCUMENT_TYPES) => types.map((t) => ({ value: t, label: documentTypeLabel(t) }));
export const authorityOptions = (names = AUTHORITY_NAMES) => names.map((a) => ({ value: a, label: a }));

export const MATCH_LABELS = Object.freeze({
    matches: 'Applies to this company',
    unknown: 'Applicability not classified',
    not_matching: 'Does not match this company',
});
export const MATCH_COLORS = Object.freeze({ matches: 'green', unknown: 'default', not_matching: 'red' });

export const RELEVANCE_LABELS = Object.freeze({ high: 'High', medium: 'Medium', low: 'Low', not_applicable: 'Not applicable' });

export const PAGE_SIZE = 25;

/** DD-MM-YYYY text -> ISO date (YYYY-MM-DD), or null when it is not a real calendar date. */
export function ddMmYyyyToIso(text) {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(text || '').trim());
    if (!m) return null;
    const [, d, mo, y] = m;
    const probe = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
    if (probe.getUTCFullYear() !== Number(y) || probe.getUTCMonth() !== Number(mo) - 1 || probe.getUTCDate() !== Number(d)) return null;
    return `${y}-${mo}-${d}`;
}

/** Query object for GET /regulatory-content; empty values are dropped by the request layer. */
export function buildListQuery(filters, { admin = false, limit = PAGE_SIZE, offset = 0 } = {}) {
    const q = { limit, offset };
    if (filters.authority) q.authority = filters.authority;
    if (filters.documentType) q.documentType = filters.documentType;
    const since = ddMmYyyyToIso(filters.since);
    if (since) q.since = since;
    if (filters.q && filters.q.trim()) q.q = filters.q.trim();
    if (filters.includeSuperseded) q.includeSuperseded = 'true';
    if (filters.topic) q.topic = filters.topic;
    if (filters.domain) q.domain = filters.domain;
    // `status` is an admin-only filter; a non-admin sending it gets a 403.
    if (admin && filters.status) q.status = filters.status;
    return q;
}

export const sinceInvalid = (text) => !!String(text || '').trim() && ddMmYyyyToIso(text) === null;

export const MIN_MANUAL_TEXT_CHARS = 200;

/** Client-side mirror of the create route's checks (the API re-validates). Returns a list of messages. */
export function validateCreateForm(f) {
    const errs = [];
    if (!f.authorityName) errs.push('Choose the issuing authority.');
    if (!f.documentType) errs.push('Choose the document type.');
    if (!String(f.officialDocumentNumber || '').trim()) errs.push('Enter the reference number printed on the document.');
    if (!String(f.title || '').trim()) errs.push('Enter the title.');
    if (String(f.text || '').trim().length < MIN_MANUAL_TEXT_CHARS) errs.push(`Paste the official text (at least ${MIN_MANUAL_TEXT_CHARS} characters).`);
    if (!/^https:\/\//i.test(String(f.sourceUrl || '').trim())) errs.push("The official source link must be an https URL on the regulator's own site.");
    if (!ddMmYyyyToIso(f.publishedDate)) errs.push('Enter the issue date as DD-MM-YYYY.');
    if (String(f.effectiveDate || '').trim() && !ddMmYyyyToIso(f.effectiveDate)) errs.push('Enter the effective date as DD-MM-YYYY, or leave it empty.');
    return errs;
}

export function buildCreateBody(f) {
    const body = {
        authorityName: f.authorityName,
        documentType: f.documentType,
        officialDocumentNumber: f.officialDocumentNumber.trim(),
        title: f.title.trim(),
        text: f.text,
        sourceUrl: f.sourceUrl.trim(),
        publishedDate: ddMmYyyyToIso(f.publishedDate),
    };
    if (String(f.effectiveDate || '').trim()) body.effectiveDate = ddMmYyyyToIso(f.effectiveDate);
    const scope = String(f.jurisdictionScope || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (scope.length) body.jurisdictionScope = scope;
    if (Array.isArray(f.topicCodes) && f.topicCodes.length) body.topicCodes = f.topicCodes;
    return body;
}

/** Draft with AI needs enough stored text (server: 422 UNGROUNDED under minGroundingChars). Returns a reason or null. */
export function draftBlockReason(item, limits) {
    const min = limits?.minGroundingChars ?? 600;
    // A pending source revision is what the next draft is made from.
    const chars = Number.isFinite(item?.pendingRevision?.textChars) ? item.pendingRevision.textChars : typeof item?.text === 'string' ? item.text.length : 0;
    if (chars < min) return `The stored text is ${chars} characters; at least ${min} are needed to ground a draft. Add the full official text first.`;
    if (Array.isArray(item?.pendingDraftIds) && item.pendingDraftIds.length > 0) return 'A draft is already waiting for review. Approve or reject it first.';
    return null;
}

const errorCode = (err) => err?.details?.code || err?.code;

export function describeEnrichError(err) {
    const code = errorCode(err);
    if (err?.status === 422 && code === 'UNGROUNDED') {
        const { chars, minChars } = err.details || {};
        return `The stored text is too short to ground a draft${Number.isFinite(chars) && Number.isFinite(minChars) ? ` (${chars} of ${minChars} characters)` : ''}. Add the full official text first.`;
    }
    if (err?.status === 409 && code === 'ITEM_HAS_PENDING') return 'A draft is already waiting for review for this document. Approve or reject it first.';
    if (err?.status === 503) return 'AI drafting is paused because the daily usage cap cannot be checked right now. Try again shortly.';
    if (err?.status === 409 && code === 'QUEUE_FULL') return 'The review queue is full. Review or reject some pending drafts before drafting more.';
    return userMessageForError(err);
}

const SOD_TEXT = 'A different platform admin must approve this draft, because you triggered it.';
const IDENTITY_TEXT = 'Your account is not provisioned in PolicyLens, so your identity could not be resolved. Ask a platform admin to invite you.';

/**
 * Failed approve -> screen text. `reload`: the draft on screen is stale or gone, reload the queue.
 * `separation`: the approver triggered this draft (a different admin must approve).
 */
export function describeApproveError(err) {
    const code = errorCode(err);
    if (err?.status === 409 && code === 'DRAFT_CHANGED') return { message: 'The draft changed since you opened it. The queue has been reloaded: review the current draft and approve again.', reload: true };
    if (err?.status === 409 && code === 'STALE_SOURCE') return { message: 'The regulation text changed after this draft was produced. Reject it and draft again.', reload: true, itemChanged: true };
    if (err?.status === 409 && code === 'ALREADY_REVIEWED') return { message: 'This draft has already been reviewed. The queue has been reloaded.', reload: true };
    if (err?.status === 409 && code === 'ITEM_CHANGED') return { message: 'The item changed after this draft was created; regenerate the draft.', reload: true, itemChanged: true };
    if (err?.status === 409 && code === 'STALE') return { message: 'The regulation changed while you were approving. The queue has been reloaded: review it and approve again.', reload: true };
    if (err?.status === 422 && code === 'DRAFT_UNREADABLE') return { message: 'This draft is unreadable and cannot be approved. Reject it and draft again.', reload: true };
    if (err?.status === 403 && code === 'SEPARATION_OF_DUTIES') return { message: SOD_TEXT, reload: false, separation: true, breakGlassAvailable: err?.details?.breakGlassAvailable };
    if (code === 'BREAK_GLASS_DISABLED' || code === 'BREAK_GLASS_REFUSED') return { message: 'A different admin must approve; break-glass is not enabled.', reload: false, separation: true, breakGlassAvailable: false };
    if (code === 'BREAK_GLASS_REASON_REQUIRED') return { message: 'Break-glass needs a reason of at least 15 characters.', reload: false, separation: true };
    if (err?.status === 403 && err?.code === 'FORBIDDEN' && !err?.details?.code) return { message: IDENTITY_TEXT, reload: false };
    return { message: userMessageForError(err), reload: err?.status === 404 };
}

/** The draft hash is echoed verbatim; break-glass travels only when a valid reason accompanies it. */
export function buildApproveBody(draftHash, { breakGlass = false, breakGlassReason = '' } = {}) {
    const body = { draftHash };
    if (breakGlass && breakGlassReasonValid(breakGlassReason)) {
        body.breakGlass = true;
        body.breakGlassReason = String(breakGlassReason).trim();
    }
    return body;
}

export const REJECT_REASON_MIN = 5;
export const REJECT_REASON_MAX = 1000;
export const rejectReasonValid = (r) => {
    const n = String(r || '').trim().length;
    return n >= REJECT_REASON_MIN && n <= REJECT_REASON_MAX;
};

export function describeSupersedeError(err) {
    const code = errorCode(err);
    if (err?.status === 409 && code === 'ALREADY_SUPERSEDED') return 'This regulation is already superseded.';
    if (err?.status === 409 && code === 'REPLACEMENT_NOT_PUBLISHED') return 'The replacement must be published first. Approve it, then mark the old document superseded.';
    return userMessageForError(err);
}

/** Per-source ingest result -> one honest line (counts, or the reason the source failed). */
export function describeIngestResult(source, r) {
    if (r?.error) return `${source.toUpperCase()}: ${r.error}`;
    const skipped = Object.values(r?.skipped || {}).reduce((a, b) => a + (Number(b) || 0), 0);
    const changed = r?.contentChangedSinceApproval ? `, ${r.contentChangedSinceApproval} changed since approval` : '';
    return `${source.toUpperCase()}: ${r?.feedItems ?? 0} in feed, ${r?.created ?? 0} new, ${r?.updated ?? 0} updated, ${r?.unchanged ?? 0} unchanged${changed}, ${skipped} skipped, ${r?.failed ?? 0} failed.`;
}

/** Applicable-regulations query: no `authorities` is sent unless the advisor changed the default selection. */
export function buildApplicableQuery({ override, includeNonMatching, limit = PAGE_SIZE, offset = 0 }) {
    const q = { limit, offset };
    if (Array.isArray(override)) q.authorities = override.join(',');
    if (includeNonMatching) q.includeNonMatching = 'true';
    return q;
}

/** True when generated text was dropped for carrying links, markup or emails. */
export const draftDiscardedUnsafeText = (draft) => (draft?.verification?.obligationsDroppedUnsafeText ?? 0) > 0;

/** Fields a 400 can name (markup in a manual title / document number). Reads details.field, else a message that starts with the field name. */
export function fieldFromError(err) {
    const known = ['title', 'officialDocumentNumber', 'text'];
    const f = err?.details?.field;
    if (known.includes(f)) return f;
    const m = /^(title|officialDocumentNumber|text)\b/.exec(err?.message || '');
    return m ? m[1] : null;
}

const PUBLICATION_CONFLICTS = {
    NOT_PUBLISHED: 'This regulation has no approved summary, so there is nothing to unpublish.',
    ALREADY_UNPUBLISHED: 'This regulation is already unpublished.',
    NOT_UNPUBLISHED: 'This regulation is not unpublished.',
    STALE: 'The regulation changed at the same time; reload it and try again.',
};
export function describePublicationError(err) {
    const code = errorCode(err);
    if (err?.status === 409 && PUBLICATION_CONFLICTS[code]) return PUBLICATION_CONFLICTS[code];
    return userMessageForError(err);
}

/** A 404 on applicable-regulations means the caller is not on the project; it is shown as absence, not as a fault. */
export const isQuietNotFound = (err) => err?.status === 404;
